import { ExpiringTokenCache } from '@suntex.core/auth';

/**
 * A Cognitive Services authorization token, as returned by the backend speech
 * token endpoint. Structurally compatible with the generated API client's
 * `Entities_Speech_GetSpeechToken_Response`.
 */
export interface SpeechToken {
  token: string;
  region: string;
  expiresOn: Date;
}

export interface ConfigureSpeechOptions {
  /** Fetches a fresh speech token (e.g. `apiClient.get_Speech_Token`). */
  fetchToken: () => Promise<SpeechToken>;
}

// Azure issue tokens live 10 minutes. The server only refreshes them every 9 minutes,
// so refreshing before then will just give back the same token.
// eslint-disable-next-line no-magic-numbers
const REFRESH_THRESHOLD_MS = 1 * 60 * 1000;
// eslint-disable-next-line no-magic-numbers
const REFRESH_COOLDOWN_MS = 30 * 1000;

let cache: ExpiringTokenCache<SpeechToken> | null = null;

/**
 * Wires speech synthesis to a source of authorization tokens. Must be called
 * once (e.g. from the app framework, with the authenticated API client) before
 * {@link synthesizeSpeech} can produce audio.
 */
export function configureSpeech({ fetchToken }: ConfigureSpeechOptions): void {
  cache = new ExpiringTokenCache<SpeechToken>({
    fetch: fetchToken,
    getExpiryMs: (value) => new Date(value.expiresOn).getTime(),
    refreshThresholdMs: REFRESH_THRESHOLD_MS,
    refreshCooldownMs: REFRESH_COOLDOWN_MS,
  });
}

/**
 * Returns a valid speech token, or `null` when {@link configureSpeech} has not
 * been called. Rotates the token in the background as it nears expiry.
 */
export function getSpeechToken(): Promise<SpeechToken | null> {
  return cache ? cache.get() : Promise.resolve(null);
}
